import type { ComponentPropsWithoutRef } from 'react';

import { SpinnerSize, type SpinnerSizeType } from './types';
import { cn } from '../../utils/cn';

interface SpinnerProps extends ComponentPropsWithoutRef<'div'> {
    size?: SpinnerSizeType;
    text?: string;
}

const sizeConfig: Record<SpinnerSizeType, { containerClass: string; size: number; strokeWidth: number }> = {
    [SpinnerSize.EXTRA_LARGE]: { containerClass: 'w-16 h-16', size: 64, strokeWidth: 6 },
    [SpinnerSize.LARGE]: { containerClass: 'w-12 h-12', size: 48, strokeWidth: 5 },
    [SpinnerSize.MEDIUM]: { containerClass: 'w-8 h-8', size: 32, strokeWidth: 4 },
    [SpinnerSize.SMALL]: { containerClass: 'w-5 h-5', size: 20, strokeWidth: 3 },
};

const textSizeStyles: Record<SpinnerSizeType, string> = {
    [SpinnerSize.EXTRA_LARGE]: 'text-lg',
    [SpinnerSize.LARGE]: 'text-base',
    [SpinnerSize.MEDIUM]: 'text-sm',
    [SpinnerSize.SMALL]: 'text-xs',
};

/**
 * A spinner loading indicator.
 *
 * @param size - Size of the spinner. Defaults to {@link SpinnerSize.MEDIUM}.
 * @param text - Optional text to display below the spinner.
 * @param className - Additional CSS classes to merge with default styles.
 *
 * @example
 * <Spinner />
 *
 * @example
 * <Spinner size={SpinnerSize.LARGE} text="Loading..." />
 *
 * @see {@link SpinnerSize} for all available sizes.
 *
 */
export const Spinner: React.FC<SpinnerProps> = ({ className, size = SpinnerSize.MEDIUM, text, ...props }) => {
    const config = sizeConfig[size];
    const radius = (config.size - config.strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const arc = circumference * 0.25;

    return (
        <div
            className={cn('inline-flex flex-col items-center gap-2', className)}
            role="status"
            aria-label={text ?? 'Loading'}
            {...props}
        >
            <div className={cn('relative', config.containerClass)}>
                <svg
                    className="animate-spin"
                    style={{ animationDuration: '1s' }}
                    width={config.size}
                    height={config.size}
                    viewBox={`0 0 ${config.size} ${config.size}`}
                >
                    <circle
                        cx={config.size / 2}
                        cy={config.size / 2}
                        r={radius}
                        fill="none"
                        stroke="#e5e7eb"
                        strokeWidth={config.strokeWidth}
                    />
                    <circle
                        cx={config.size / 2}
                        cy={config.size / 2}
                        r={radius}
                        fill="none"
                        stroke="#1F2B43"
                        strokeWidth={config.strokeWidth}
                        strokeLinecap="round"
                        strokeDasharray={`${arc} ${circumference - arc}`}
                        strokeDashoffset={arc}
                    />
                </svg>
            </div>
            {text && <span className={cn('text-gray-600', textSizeStyles[size])}>{text}</span>}
        </div>
    );
};
